import { css } from '@compiled/react';
import type { FC } from 'react';
import React, { memo, useMemo } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { EventHandlers } from '@atlaskit/editor-common/EventHandlers';
import type { ExtensionParams } from '@atlaskit/editor-common/extensions/extension-handler';
import type ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';
import { RendererActionsContext } from '@atlaskit/renderer/actions/renderer-actions-context';
import { ValidationContextProvider } from '@atlaskit/renderer/validation-context';

import { ADFRenderer } from '@confluence/adf-renderer/entry-points/ADFRenderer.ts';
import type { ADFRendererProps } from '@confluence/adf-renderer/entry-points/ADFRendererComponent.ts';
import { expValEquals } from '@confluence/feature-experiments';

import type { LegacyContentMacroParams } from './LegacyContentMacroParams.ts';

export interface NBMRendererServerProps {
	contentId?: string;
	contentMode?: ADFRendererProps['contentMode'];
	node: ExtensionParams<LegacyContentMacroParams>;
}

export interface NBMRendererContentProps {
	allowAnnotations?: boolean;
	annotationProvider?: ADFRendererProps['annotationProvider'];
	appearance: 'full-width' | 'max';
	contentId?: string;
	contentMode?: ADFRendererProps['contentMode'];
	dataProviders?: ProviderFactory;
	adfDocument: DocNode;
	eventHandlers?: EventHandlers;
	onSetLinkTarget?: (url: string) => '_blank' | undefined;
}

const DEFAULT_DOCUMENT: DocNode = {
	version: 1,
	type: 'doc',
	content: [
		{
			type: 'paragraph',
			content: [],
		},
	],
};

export const resolveNBMRenderConfig = (
	node: ExtensionParams<LegacyContentMacroParams>,
): Pick<NBMRendererContentProps, 'adfDocument' | 'appearance'> => ({
	adfDocument: node?.parameters?.nestedContent || DEFAULT_DOCUMENT,
	appearance:
		expValEquals('editor_tinymce_full_width_mode', 'isEnabled', true) ||
		expValEquals('confluence_max_width_content_appearance', 'isEnabled', true)
			? 'max'
			: 'full-width',
});

const collapsedEmptyLists = css({
	// Hide bullets for deeply nested lists without sibling content so they look indented.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
	'& li:has(> ul:only-child), & li:has(> ol:only-child)': {
		listStyleType: 'none',
		paddingLeft: 0,
	},
});

const stableTableHydrationLayout = css({
	// Match the formatting boundary created when table overflow sentinels are added after hydration.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
	'&:has(.pm-table-wrapper)::before': {
		content: '""',
		display: 'table',
	},
});

export const NBMRendererContent: FC<NBMRendererContentProps> = ({
	allowAnnotations,
	annotationProvider,
	appearance,
	contentId,
	contentMode,
	dataProviders,
	adfDocument,
	eventHandlers,
	onSetLinkTarget,
}) => {
	const validationContextValue = useMemo(() => ({ skipValidation: true }), []);

	return (
		<ValidationContextProvider value={validationContextValue}>
			<RendererActionsContext>
				<div css={[collapsedEmptyLists, stableTableHydrationLayout]}>
					<ADFRenderer
						annotationProvider={annotationProvider}
						allowAnnotations={allowAnnotations}
						appearance={appearance}
						allowHeadingAnchorLinks={false}
						contentId={contentId}
						dataProviders={dataProviders}
						document={adfDocument}
						extensionHandlers={undefined}
						contentMode={contentMode}
						eventHandlers={eventHandlers}
						onSetLinkTarget={onSetLinkTarget}
					/>
				</div>
			</RendererActionsContext>
		</ValidationContextProvider>
	);
};

/**
 * Server/first-hydration renderer for fixed level-0 SSR. It intentionally avoids the
 * client provider hooks because they can start queries that are unnecessary when no nested
 * extension is present.
 */
export const NBMRendererServer: FC<NBMRendererServerProps> = memo(
	({ contentId, contentMode, node }) => {
		const { adfDocument, appearance } = resolveNBMRenderConfig(node);

		return (
			<NBMRendererContent
				adfDocument={adfDocument}
				appearance={appearance}
				contentId={contentId}
				contentMode={contentMode}
			/>
		);
	},
);
